import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import JobPhotos from "./JobPhotos";
import { getPosition } from "@/lib/geo";

export default function JobPhotosDialog({ job, mode, user, onClose, onDone }) {
  const [count, setCount] = useState(null);
  const [saving, setSaving] = useState(false);
  const completing = mode === "complete";
  const afterCount = (count ?? job?.photos ?? []).filter((p) => p.kind === "after").length;

  const complete = async () => {
    setSaving(true);
    try {
      const geo = await getPosition();
      await api.put(`/jobs/${job.id}`, { status: "completed", ...(geo || {}) });
      toast.success("Nice work! Job marked complete and the office has been notified");
      window.dispatchEvent(new Event("job-status-changed"));
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const close = () => { setCount(null); if (count) onDone(); onClose(); };

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid={completing ? "complete-job-dialog" : "job-photos-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{completing ? "Finish this clean" : "Job photos"}</DialogTitle>
          <DialogDescription>
            {job?.customer?.name}{job?.package?.name ? ` · ${job.package.name}` : ""}
            {completing ? " — add a few after photos so the office can see the finished job (optional)." : ""}
          </DialogDescription>
        </DialogHeader>
        {job && <JobPhotos job={job} user={user} highlight={completing ? "after" : "before"} onChange={setCount} />}
        <DialogFooter>
          {completing ? (
            <Button onClick={complete} disabled={saving} data-testid="complete-job-confirm" className="rounded-sm gap-2">
              <CheckCircle2 className="w-4 h-4" /> {saving ? "Saving…" : afterCount ? "Finish job" : "Finish job without photos"}
            </Button>
          ) : (
            <Button variant="outline" onClick={close} data-testid="job-photos-close" className="rounded-sm">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
